import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Owner } from "../../lib/adminApi";
import {
  CreditCard,
  Banknote,
  Calendar,
  DollarSign,
  Clock,
  Store,
  User,
} from "lucide-react";

interface PaymentFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (paymentData: {
    ownerId: string;
    amount: number;
    periodCovered: string;
    paymentMethod: string;
    referenceNumber?: string;
    receiptImageUrl?: string;
    adminNotes?: string;
  }) => Promise<void>;
  owners: Owner[];
  selectedOwnerId?: string;
}

export function PaymentFormModal({
  open,
  onClose,
  onSubmit,
  owners,
  selectedOwnerId,
}: PaymentFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ownerId: selectedOwnerId || "",
    amount: "",
    periodCovered: "1_month",
    paymentMethod: "transfer",
    referenceNumber: "",
    receiptImageUrl: "",
    adminNotes: "",
  });

  // Reset form when modal opens/closes or selectedShopId changes
  useEffect(() => {
    if (open) {
      setFormData({
        ownerId: selectedOwnerId || "",
        amount: "",
        periodCovered: "1_month",
        paymentMethod: "transfer",
        referenceNumber: "",
        receiptImageUrl: "",
        adminNotes: "",
      });
    }
  }, [open, selectedOwnerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.ownerId ||
      !formData.amount ||
      !formData.periodCovered ||
      !formData.paymentMethod
    ) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        ownerId: formData.ownerId,
        amount: parseFloat(formData.amount),
        periodCovered: formData.periodCovered,
        paymentMethod: formData.paymentMethod,
        referenceNumber: formData.referenceNumber || undefined,
        receiptImageUrl: formData.receiptImageUrl || undefined,
        adminNotes: formData.adminNotes || undefined,
      });
      onClose();
    } catch (error) {
      console.error("Error submitting payment:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedOwner = () => {
    return owners.find((owner) => owner.id === formData.ownerId);
  };

  const selectedOwner = getSelectedOwner();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Registrar Pago Manual
          </DialogTitle>
          <DialogDescription>
            Registra un pago para activar o extender la suscripción de un
            negocio
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información del Negocio */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Información del Negocio</h3>

            <div className="space-y-2">
              <Label htmlFor="ownerId">Seleccionar Negocio *</Label>
              <Select
                value={formData.ownerId}
                onValueChange={(value) =>
                  setFormData({ ...formData, ownerId: value })
                }
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un negocio" />
                </SelectTrigger>
                <SelectContent>
                  {owners.map((owner) => (
                    <SelectItem key={owner.id} value={owner.id}>
                      <div className="flex items-center gap-2">
                        <Store className="h-4 w-4" />
                        <span>{owner.businessName}</span>
                        <Badge
                          variant="outline"
                          className={`ml-2 ${
                            owner.accountStatus === "active"
                              ? "border-green-200 text-green-700"
                              : owner.accountStatus === "trial"
                                ? "border-yellow-200 text-yellow-700"
                                : "border-red-200 text-red-700"
                          }`}
                        >
                          {owner.accountStatus === "active"
                            ? "Activo"
                            : owner.accountStatus === "trial"
                              ? "Trial"
                              : "Suspendido"}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedOwner && (
              <Card className="p-4 bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Dueño</div>
                    <div className="font-medium flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {selectedOwner.ownerName}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Email</div>
                    <div className="font-medium text-sm">
                      {selectedOwner.email}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Estado Actual</div>
                    <div className="font-medium">
                      <Badge
                        className={
                          selectedOwner.accountStatus === "active"
                            ? "bg-green-100 text-green-800"
                            : selectedOwner.accountStatus === "trial"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }
                      >
                        {selectedOwner.accountStatus === "active"
                          ? "Activo"
                          : selectedOwner.accountStatus === "trial"
                            ? "En Trial"
                            : "Suspendido"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Plan</div>
                    <div className="font-medium capitalize">
                      {selectedOwner.planType}
                    </div>
                  </div>
                  {selectedOwner.subscriptionEndDate && (
                    <div className="col-span-2">
                      <div className="text-sm text-gray-600">
                        Suscripción actual termina
                      </div>
                      <div className="font-medium flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {new Date(
                          selectedOwner.subscriptionEndDate,
                        ).toLocaleDateString("es-ES")}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Detalles del Pago */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Detalles del Pago</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Monto (CUP) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    placeholder="50.00"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="periodCovered">Período cubierto *</Label>
                <Select
                  value={formData.periodCovered}
                  onValueChange={(value) =>
                    setFormData({ ...formData, periodCovered: value })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1_month">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />1 mes (2000 CUP)
                      </div>
                    </SelectItem>
                    <SelectItem value="3_months">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />3 meses (6000 CUP)
                      </div>
                    </SelectItem>
                    <SelectItem value="6_months">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />6 meses (12000 CUP)
                      </div>
                    </SelectItem>
                    <SelectItem value="1_year">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />1 año (21600 CUP)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentMethod">Método de pago *</Label>
                <Select
                  value={formData.paymentMethod}
                  onValueChange={(value) =>
                    setFormData({ ...formData, paymentMethod: value })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona método" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transfer">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4" />
                        Transferencia bancaria
                      </div>
                    </SelectItem>
                    <SelectItem value="cash">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Efectivo
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referenceNumber">Número de referencia</Label>
                <Input
                  id="referenceNumber"
                  placeholder="TRF-123456"
                  value={formData.referenceNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      referenceNumber: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="receiptImageUrl">
                URL del comprobante (opcional)
              </Label>
              <Input
                id="receiptImageUrl"
                placeholder="https://ejemplo.com/comprobante.jpg"
                value={formData.receiptImageUrl}
                onChange={(e) =>
                  setFormData({ ...formData, receiptImageUrl: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminNotes">Notas administrativas</Label>
              <Textarea
                id="adminNotes"
                placeholder="Información adicional sobre este pago..."
                value={formData.adminNotes}
                onChange={(e) =>
                  setFormData({ ...formData, adminNotes: e.target.value })
                }
                rows={3}
              />
            </div>
          </div>

          {/* Resumen */}
          {formData.amount && (
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Resumen</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monto a pagar:</span>
                  <span className="font-bold text-blue-700">
                    {parseFloat(formData.amount).toLocaleString("es-CU")} CUP
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Período:</span>
                  <span className="font-medium">
                    {formData.periodCovered === "1_month"
                      ? "1 mes"
                      : formData.periodCovered === "3_months"
                        ? "3 meses"
                        : formData.periodCovered === "6_months"
                          ? "6 meses"
                          : "1 año"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Método:</span>
                  <span className="font-medium">
                    {formData.paymentMethod === "transfer"
                      ? "Transferencia"
                      : "Efectivo"}
                  </span>
                </div>
                <div className="pt-2 border-t border-blue-200">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Nueva fecha de término:
                    </span>
                    <span className="font-bold text-green-700">
                      {(() => {
                        const today = new Date();
                        let endDate = new Date(today);

                        if (formData.periodCovered === "1_month") {
                          endDate.setMonth(endDate.getMonth() + 1);
                        } else if (formData.periodCovered === "3_months") {
                          endDate.setMonth(endDate.getMonth() + 3);
                        } else if (formData.periodCovered === "6_months") {
                          endDate.setMonth(endDate.getMonth() + 6);
                        } else if (formData.periodCovered === "1_year") {
                          endDate.setFullYear(endDate.getFullYear() + 1);
                        }

                        return endDate.toLocaleDateString("es-ES", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        });
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.ownerId || !formData.amount}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Registrando...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Registrar Pago
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
